// Zod validation schemas for forms

import { z } from 'zod';
import { VALIDATION } from './constants';
import { NudgeCategory, NudgeScheduleType, NudgeType } from '@/lib/sdk/calendar/types';

// Login Schema
export const loginSchema = z.object({
  country_code: z.string().min(1, 'Country code is required'),
  phone_number: z
    .string()
    .min(VALIDATION.PHONE_MIN_LENGTH, `Phone number must be at least ${VALIDATION.PHONE_MIN_LENGTH} digits`)
    .max(VALIDATION.PHONE_MAX_LENGTH, `Phone number must not exceed ${VALIDATION.PHONE_MAX_LENGTH} digits`)
    .regex(/^\d+$/, 'Phone number must contain only digits'),
});

export type LoginFormData = z.infer<typeof loginSchema>;

// OTP Verification Schema
export const otpSchema = z.object({
  otp: z
    .string()
    .length(VALIDATION.OTP_LENGTH, `OTP must be exactly ${VALIDATION.OTP_LENGTH} digits`)
    .regex(/^\d+$/, 'OTP must contain only digits'),
});

export type OTPFormData = z.infer<typeof otpSchema>;

// Feature Permissions Schema
const featurePermissionsSchema = z.object({
  reports: z.array(z.enum(['view', 'add', 'edit', 'delete', 'share'])).optional(),
  team: z.array(z.enum(['view', 'add', 'edit', 'delete'])).optional(),
  workflows: z.array(z.enum(['view_instant', 'edit_data', 'add_data', 'add', 'edit', 'delete', 'ai_assist'])).optional(),
  master_data: z.array(z.enum(['view', 'add', 'edit', 'delete', 'add_fields', 'edit_fields', 'delete_fields', 'add_categories', 'edit_categories', 'delete_categories', 'import', 'export'])).optional(),
  ai_chat: z.array(z.enum(['use'])).optional(),
  nudges: z.array(z.enum(['view', 'add', 'edit', 'delete'])).optional(),
}).optional();

// Team Member Schema
export const teamMemberSchema = z.object({
  country_code: z.string().min(1, 'Country code is required'),
  phone_number: z
    .string()
    .min(VALIDATION.PHONE_MIN_LENGTH, `Phone number must be at least ${VALIDATION.PHONE_MIN_LENGTH} digits`)
    .max(VALIDATION.PHONE_MAX_LENGTH, `Phone number must not exceed ${VALIDATION.PHONE_MAX_LENGTH} digits`)
    .regex(/^\d+$/, 'Phone number must contain only digits'),
  name: z
    .string()
    .min(VALIDATION.NAME_MIN_LENGTH, `Name must be at least ${VALIDATION.NAME_MIN_LENGTH} characters`)
    .max(VALIDATION.NAME_MAX_LENGTH, `Name must not exceed ${VALIDATION.NAME_MAX_LENGTH} characters`)
    .regex(/^[a-zA-Z\s]+$/, 'Name can only contain letters and spaces'),
  org_code: z.string().optional(),
  nickname: z.string().optional(),
  org_permissions: z.enum(['read_write', 'admin']).default('read_write'),
  feature_permissions: featurePermissionsSchema,
  adhoc_tasks_enabled: z.boolean().default(true),
  instant_task_scope: z.enum(['all', 'self', 'members']).optional(),
  instant_task_members: z.array(z.string()).optional(),
}).refine(
  // Only checked while the "Can view tasks of" control is shown (a non-admin with View Instant Tasks).
  (data) =>
    data.org_permissions === 'admin' ||
    !data.feature_permissions?.workflows?.includes('view_instant') ||
    data.instant_task_scope !== 'members' ||
    (data.instant_task_members?.length ?? 0) > 0,
  { message: 'Select at least one member', path: ['instant_task_members'] },
);

export type TeamMemberFormData = z.infer<typeof teamMemberSchema>;
export type TeamMemberFormInput = z.input<typeof teamMemberSchema>;

// Task Edit Schema
export const taskEditSchema = z.object({
  tasks: z.array(
    z.object({
      rep_id: z.number(),
      note_id: z.string().min(1, 'Note is required'),
      value: z.string().min(1, 'Value is required'),
    })
  ),
});

export type TaskEditFormData = z.infer<typeof taskEditSchema>;

// Report Share Schema
export const reportShareSchema = z.object({
  userIds: z.array(z.string()).min(1, 'Select at least one team member'),
});

export type ReportShareFormData = z.infer<typeof reportShareSchema>;

// Report Form Schema
export const reportFormSchema = z.object({
  title: z
    .string()
    .min(1, 'Title is required')
    .max(100, 'Title must not exceed 100 characters'),
  description: z
    .string()
    .min(1, 'Description is required')
    .max(500, 'Description must not exceed 500 characters'),
  public_url: z
    .string()
    .min(1, 'Share URL is required')
    .url('Must be a valid URL'),
});

export type ReportFormData = z.infer<typeof reportFormSchema>;


export const nudgeSchema = z.object({
  nudge_category: z.nativeEnum(NudgeCategory),
  nudge_type: z.nativeEnum(NudgeType),
  time_zone: z.string().min(1, 'Time zone is required'),
  nudge_time: z.string().min(1, 'Event time is required'),
  chatbot_id: z.string().optional(),
  schedule_type: z.nativeEnum(NudgeScheduleType),
  nudge_date: z.string().optional(),
  day_of_week: z.coerce.number<number>().int().min(0).max(6).optional(),
  day_of_month: z.coerce.number<number>().int().min(1).max(31).optional(),
  follow_up_enabled: z.boolean().optional(),
}).refine((data) => {
  if (data.nudge_category === NudgeCategory.Chatbot) {
    return !!data.chatbot_id;
  }
  return true;
}, {
  message: "Chatbot is required for chatbot events",
  path: ["chatbot_id"],
}).refine((data) => {
  if (data.schedule_type === NudgeScheduleType.ONE_TIME) {
    return !!data.nudge_date;
  }
  return true;
}, {
  message: "Date is required for one-time nudges",
  path: ["nudge_date"],
}).refine((data) => {
  if (data.schedule_type === NudgeScheduleType.WEEKLY) {
    return data.day_of_week !== undefined;
  }
  return true;
}, {
  message: "Day of week is required for weekly nudges",
  path: ["day_of_week"],
}).refine((data) => {
  if (data.schedule_type === NudgeScheduleType.MONTHLY) {
    return data.day_of_month !== undefined;
  }
  return true;
}, {
  message: "Day of month is required for monthly nudges",
  path: ["day_of_month"],
});

export type NudgeFormData = z.infer<typeof nudgeSchema>;
export type NudgeFormInput = z.input<typeof nudgeSchema>;


export const userSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  country_code: z.string().min(1, 'Country code is required'),
  phone_number: z.string().min(1, 'Phone number is required')
    .regex(/^\d{10}$/, 'Phone number must be 10 digits'),
  chatbot_id: z.string().optional(),
  org_code: z.string().optional(),
  nickname: z.string().optional(),
  org_permissions: z.enum(['read_write', 'admin']).default('read_write'),
  feature_permissions: featurePermissionsSchema,
});

export type UserFormData = z.infer<typeof userSchema>;
export type UserFormInput = z.input<typeof userSchema>;

